import { createRoute, lazyRouteComponent } from '@tanstack/react-router'

import { guardRoute } from '../auth/guard.ts'
import { NAV_PARAM_PATHS } from '../navigation/paths.ts'
import { numericIdParam, parseOneOf } from './params.ts'
import { rootRoute } from './root.tsx'

/** The lists that link a name here. The profile names the one it came from and goes back to
 *  it: two lists reach it, so the page cannot guess. */
export const PLAYER_ORIGINS = ['roster', 'players', 'team'] as const

export type PlayerOrigin = (typeof PLAYER_ORIGINS)[number]

export interface PlayerSearch {
  /** Optional rather than nullable: the router writes a `null` back as `?from=null`. */
  readonly from?: PlayerOrigin
}

// The profile a list opens from a name. Why it asks for a session and no role:
// apps/frontend/docs/quattro-profili-sotto-me.md.
export const playerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: NAV_PARAM_PATHS.player,
  params: numericIdParam('personId'),
  staticData: { opensOnHero: true },
  validateSearch: (search: Record<string, unknown>): PlayerSearch => ({
    from: parseOneOf(PLAYER_ORIGINS, search.from) ?? undefined,
  }),
  beforeLoad: ({ context, location }) =>
    guardRoute({ queryClient: context.queryClient, currentPath: location.href }),
  component: lazyRouteComponent(() => import('../pages/player.tsx')),
})

/** One giocatore as a link target: `params` typed, so no page respells the path. `from` is
 *  the caller naming itself, which is the only way back this page can offer. */
export function playerProfileLink(personId: number, from: PlayerOrigin) {
  return { to: NAV_PARAM_PATHS.player, params: { personId }, search: { from } } as const
}

/** The profile from a page under it, which no list opened: no `from`, so no way back on it.
 *  `exact`, or the page under it would mark it the current one. */
export function playerPageLink(personId: number) {
  return {
    to: NAV_PARAM_PATHS.player,
    params: { personId },
    search: {},
    activeOptions: { exact: true },
  } as const
}
